import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import ArticleCard from "@/components/base/ArticleCard";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";
import { getCategoryBySlug } from "@/utils/content";
import usePageMeta from "@/hooks/usePageMeta";

type SortKey = "new" | "hot";

export default function CategoryPage() {
  const { slug = "" } = useParams();
  const category = getCategoryBySlug(slug);
  const [sort, setSort] = useState<SortKey>("new");

  const list = useMemo(() => {
    const base = articles.filter((item) => item.categorySlug === slug);
    return sort === "hot"
      ? [...base].sort((a, b) => b.heat - a.heat)
      : [...base].sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [slug, sort]);

  const name = category?.name ?? "幕后内容";
  const desc = category?.desc ?? "收录与幕后相关的现场见闻与制作故事。";

  usePageMeta({
    title: `${name} - 黑料八卦${name}幕后八卦与拍摄现场花絮`,
    description: `${name}栏目聚合黑料八卦相关内容：${desc}`,
    keywords: `黑料八卦,${name},黑料八卦${category?.short ?? "幕后"},节目剧组幕后,拍摄现场花絮`,
    canonicalPath: `/category/${slug}`,
  });

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://glmrz.cn/" },
      { "@type": "ListItem", position: 2, name, item: `https://glmrz.cn/category/${slug}` },
    ],
  };

  const collectionJsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${name} - 黑料八卦`,
    description: desc,
    inLanguage: "zh-CN",
    mainEntity: {
      "@type": "ItemList",
      itemListElement: list.map((item, idx) => ({
        "@type": "ListItem",
        position: idx + 1,
        name: item.title,
        url: `https://glmrz.cn/article/${item.slug}`,
      })),
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify([breadcrumbJsonLd, collectionJsonLd]) }}
      />
      <SiteHeader />

      <main className="max-w-portal mx-auto px-4 md:px-6 pt-8 md:pt-10">
        {/* 面包屑 */}
        <nav aria-label="面包屑导航" className="flex items-center gap-2 text-xs text-foreground-500 mb-6">
          <Link to="/" className="hover:text-accent-700 transition-colors cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
          <span className="text-foreground-800 font-medium">{name}</span>
        </nav>

        <header className="pb-6 border-b-2 border-foreground-950">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div className="flex items-start gap-4">
              <span className="w-14 h-14 shrink-0 flex items-center justify-center rounded-md bg-foreground-950 text-primary-400">
                <i className={`${category?.icon ?? "ri-file-list-3-line"} text-3xl`} />
              </span>
              <div>
                <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground-950">
                  {name}
                </h1>
                <p className="text-sm text-foreground-600 leading-relaxed mt-2 max-w-2xl">{desc}</p>
              </div>
            </div>
            <span className="text-sm text-foreground-500 shrink-0">
              共 <strong className="text-foreground-950">{list.length}</strong> 篇幕后内容
            </span>
          </div>
        </header>

        {/* 分类切换 */}
        <div className="flex flex-wrap items-center gap-2 py-6">
          <Link
            to="/"
            className="px-3 py-1.5 rounded-full bg-background-100 border border-background-200 text-xs font-medium text-foreground-600 hover:border-primary-400 transition-colors cursor-pointer"
          >
            全部
          </Link>
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors cursor-pointer ${
                cat.slug === slug
                  ? "bg-foreground-950 text-background-50"
                  : "bg-background-100 border border-background-200 text-foreground-600 hover:border-primary-400"
              }`}
            >
              {cat.name}
            </Link>
          ))}
        </div>

        {/* 排序 */}
        <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200 w-fit mb-6">
          {([
            { key: "new", label: "最新发布" },
            { key: "hot", label: "最热爆料" },
          ] as { key: SortKey; label: string }[]).map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setSort(opt.key)}
              className={`px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap cursor-pointer transition-colors ${
                sort === opt.key
                  ? "bg-primary-500 text-foreground-950"
                  : "text-foreground-600 hover:text-foreground-950"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {list.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 pb-4">
            {list.map((item) => (
              <ArticleCard key={item.id} article={item} />
            ))}
          </div>
        ) : (
          <div className="py-16 text-center border border-dashed border-background-300 rounded-lg">
            <i className="ri-inbox-line text-4xl text-foreground-400" />
            <p className="text-sm text-foreground-600 mt-3">
              该栏目暂时还没有幕后稿件，先去其他栏目逛逛吧。
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 mt-5 px-5 py-2.5 rounded-md bg-foreground-950 text-background-50 text-sm font-bold whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        )}

        {/* 其他栏目 */}
        <div className="mt-12 pt-8 border-t border-background-200">
          <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">换个栏目看看</h2>
          <div className="flex flex-wrap gap-3">
            {categories
              .filter((cat) => cat.slug !== slug)
              .map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-background-100 border border-background-200 text-sm font-medium text-foreground-800 hover:border-primary-500 hover:bg-primary-50 transition-colors cursor-pointer"
                >
                  <i className={`${cat.icon} text-lg text-secondary-700`} />
                  {cat.name}
                </Link>
              ))}
          </div>
        </div>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}